import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useAuth } from '../../components/AuthProvider';
import { apiUrl } from '../../lib/api';

const fontMono = { fontFamily: "'Share Tech Mono', 'JetBrains Mono', 'Courier New', monospace" };
const fontOrbitron = { fontFamily: "'Orbitron', 'Arial', sans-serif" };

const STATUS_STYLES = {
  complete: { bg: '#071a14', text: '#2DC896', label: 'COMPLETE' },
  failed: { bg: '#1a0d0d', text: '#ff6b6b', label: 'FAILED' },
  partial: { bg: '#1a1200', text: '#EF9F27', label: 'PARTIAL' },
};

function getStatusStyle(status) {
  const s = (status || '')?.toLowerCase();
  if (s === 'complete' || s === 'completed' || s === 'success') return STATUS_STYLES?.complete;
  if (s === 'failed' || s === 'error') return STATUS_STYLES?.failed;
  return STATUS_STYLES?.partial;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' + d?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch { return dateStr; }
}

const PAGE_SIZE = 20;

const MissionHistory = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (!session?.access_token) throw new Error('Authentication session is unavailable');
        headers['Authorization'] = `Bearer ${session.access_token}`;
        const res = await fetch(apiUrl('/api/missions'), { headers });
        if (!res?.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res?.json();
        const list = data?.missions || data?.data || data || [];
        setMissions(Array.isArray(list) ? list : []);
      } catch (err) {
        setError(err?.message);
        setMissions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [session?.access_token]);

  const displayMissions = missions;

  const filtered = displayMissions?.filter(m => {
    const matchSearch = !search || (m?.title || m?.intent || m?.mission || '')?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || getStatusStyle(m?.status)?.label?.toLowerCase() === statusFilter?.toLowerCase();
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered?.length / PAGE_SIZE));
  const paginated = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleReopen = async (mission) => {
    const missionId = mission?.id;
    if (!missionId) {
      navigate('/command-center-ai-execution-interface', { state: { reopenMissionData: mission } });
      return;
    }
    try {
      const token = session?.access_token || null;
      if (!token) throw new Error('Authentication session is unavailable');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Fetch mission details
      const res = await fetch(apiUrl(`/api/missions/${missionId}`), { headers });
      if (!res?.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res?.json();
      const missionData = data?.mission || data?.data || data;

      const results = missionData?.results || [];
      const missionTitle = missionData?.user_text || missionData?.intent || missionData?.title || mission?.title || mission?.intent || '';

      // Fetch files count
      let filesCount = 0;
      let filesReady = null;
      try {
        const filesRes = await fetch(apiUrl(`/api/missions/${missionId}/files`), { headers });
        if (filesRes?.ok) {
          const filesJson = await filesRes?.json();
          const filesArr = filesJson?.files || filesJson;
          if (Array.isArray(filesArr)) {
            filesCount = filesArr?.length;
            filesReady = filesArr?.length > 0 ? filesArr : null;
          }
        }
      } catch (_) {}

      // Map results array into MissionResultsScreen format
      if (!results || results?.length === 0) {
        // No results — pass a special flag so the screen shows the empty state
        navigate('/command-center-ai-execution-interface', {
          state: {
            reopenMissionData: {
              mission_id: missionId,
              intent: missionTitle,
              noResults: true,
              actionCards: [],
              completedSteps: {},
              steps_completed: 0,
              steps_failed: 0,
              filesReady,
              filesCount,
              summary_simple: missionData?.summary_simple,
            }
          }
        });
        return;
      }

      // Map each result item into an actionCard
      const actionCards = results?.map((item) => ({
        id: item?.id,
        title: item?.action || item?.task || `Step ${item?.id || ''}`,
        output: item?.output || '',
        full_output: item?.output || '',
        description: item?.output ? item?.output?.slice(0, 200) : '',
        status: item?.status || 'complete',
        bot_name: item?.bot_name || '',
      }));

      // Also build completedSteps map for compatibility
      const completedSteps = {};
      results?.forEach((item, i) => {
        const key = item?.action || item?.task || `step-${i}`;
        completedSteps[key] = {
          id: item?.id,
          action: item?.action || item?.task || key,
          output: item?.output || '',
          status: item?.status || 'complete',
          bot_name: item?.bot_name || '',
        };
      });

      navigate('/command-center-ai-execution-interface', {
        state: {
          reopenMissionData: {
            mission_id: missionId,
            intent: missionTitle,
            actionCards,
            completedSteps,
            steps_completed: results?.length,
            steps_failed: results?.filter(r => r?.status === 'failed' || r?.status === 'error')?.length || 0,
            filesReady,
            filesCount,
            summary_simple: missionData?.summary_simple,
          }
        }
      });
    } catch (err) {
      setError(`Could not open mission: ${err?.message || 'Unknown error'}`);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#05060f', color: '#E8F4FF', position: 'relative' }}>
      <Helmet>
        <title>Mission History — RecallCore</title>
        <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&family=Share+Tech+Mono&display=swap" rel="stylesheet" />
      </Helmet>
      {/* Stars */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        {Array.from({ length: 60 }, (_, i) => (
          <div key={i} style={{ position: 'absolute', left: `${(i * 17.3) % 100}%`, top: `${(i * 23.7) % 100}%`, width: i % 4 === 0 ? 2 : 1, height: i % 4 === 0 ? 2 : 1, borderRadius: '50%', background: i % 5 === 0 ? '#2DC896' : '#ffffff', opacity: 0.06 + (i % 5) * 0.02 }} />
        ))}
      </div>
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 900, margin: '0 auto', padding: '40px 24px 80px' }}>
        {/* Back nav */}
        <button onClick={() => navigate('/command-center-ai-execution-interface')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', ...fontMono, fontSize: 11, color: '#2DC89660', marginBottom: 32, letterSpacing: '0.08em' }}>
          <ChevronLeft style={{ width: 14, height: 14 }} /> BACK TO COMMAND CENTER
        </button>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <h1 style={{ ...fontOrbitron, fontSize: 32, fontWeight: 900, color: '#FFFFFF', letterSpacing: '0.12em', marginBottom: 8 }}>MISSION HISTORY</h1>
          <div style={{ ...fontMono, fontSize: 12, color: '#2DC896', letterSpacing: '0.1em' }}>Your execution archive</div>
        </div>

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#2DC89660' }} />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e?.target?.value); setPage(1); }}
              placeholder="Search missions..."
              style={{ width: '100%', background: 'rgba(45,200,150,0.04)', border: '1px solid rgba(45,200,150,0.2)', borderRadius: 8, padding: '10px 14px 10px 36px', ...fontMono, fontSize: 12, color: '#E8F4FF', outline: 'none', boxSizing: 'border-box', caretColor: '#2DC896' }}
              onFocus={e => { e.target.style.borderColor = 'rgba(45,200,150,0.5)'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(45,200,150,0.2)'; }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e?.target?.value); setPage(1); }}
            style={{ background: 'rgba(45,200,150,0.04)', border: '1px solid rgba(45,200,150,0.2)', borderRadius: 8, padding: '10px 14px', ...fontMono, fontSize: 12, color: '#E8F4FF', outline: 'none', cursor: 'pointer', minWidth: 160 }}>
            <option value="all">All Missions</option>
            <option value="complete">Complete</option>
            <option value="failed">Failed</option>
            <option value="partial">Partial</option>
          </select>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ ...fontMono, fontSize: 12, color: '#2DC896', letterSpacing: '0.1em' }}>LOADING MISSIONS...</div>
          </div>
        )}

        {!loading && error && (
          <div style={{ textAlign: 'center', padding: '24px', marginBottom: 24, border: '1px solid rgba(255,107,107,0.35)', background: 'rgba(255,107,107,0.08)', borderRadius: 8, ...fontMono, fontSize: 12, color: '#ff6b6b' }}>
            {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && paginated?.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ ...fontOrbitron, fontSize: 16, color: '#FFFFFF', marginBottom: 12 }}>No missions executed yet.</div>
            <p style={{ ...fontMono, fontSize: 12, color: '#8a9ab5', marginBottom: 32 }}>Launch Your First Mission to see your history here.</p>
            <button onClick={() => navigate('/command-center-ai-execution-interface')}
              style={{ background: '#EF9F27', border: 'none', borderRadius: 8, padding: '12px 28px', ...fontOrbitron, fontSize: 12, fontWeight: 700, color: '#05060f', cursor: 'pointer', letterSpacing: '0.08em' }}>
              RUN FIRST MISSION →
            </button>
          </div>
        )}

        {/* Mission list */}
        {!loading && paginated?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {paginated?.map((mission, i) => {
              const missionName = mission?.user_text || mission?.title || mission?.name || mission?.mission_name || mission?.objective;
              const title = missionName || mission?.intent || mission?.mission || `Mission ${mission?.id || i + 1}`;
              const statusStyle = getStatusStyle(mission?.status);
              const operatorsCount = mission?.operators_count || mission?.steps_completed || mission?.steps?.length || 0;
              let filesCount = mission?.files_count || mission?.total_cards || 0;

              return (
                <motion.div key={mission?.id || i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  style={{ background: '#0a0d1e', border: '1px solid #1a2040', borderRadius: 10, padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...fontOrbitron, fontSize: 13, fontWeight: 600, color: '#FFFFFF', letterSpacing: '0.04em', lineHeight: 1.4 }}>{title}</div>
                      {mission?.id && (
                        <div style={{ ...fontMono, fontSize: 10, color: '#4a5568', marginTop: 3, letterSpacing: '0.04em', opacity: 0.7 }}>ID: {mission?.id}</div>
                      )}
                    </div>
                    <div style={{ ...fontMono, fontSize: 10, color: '#8a9ab5', flexShrink: 0, textAlign: 'right' }}>{formatDate(mission?.created_at || mission?.timestamp)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {operatorsCount > 0 && (
                      <div style={{ padding: '3px 10px', borderRadius: 4, background: 'rgba(45,200,150,0.1)', border: '1px solid rgba(45,200,150,0.25)', ...fontMono, fontSize: 10, color: '#2DC896' }}>
                        {operatorsCount} operators
                      </div>
                    )}
                    {filesCount > 0 && (
                      <div style={{ padding: '3px 10px', borderRadius: 4, background: 'rgba(239,159,39,0.1)', border: '1px solid rgba(239,159,39,0.25)', ...fontMono, fontSize: 10, color: '#EF9F27' }}>
                        {filesCount} files
                      </div>
                    )}
                    <div style={{ padding: '3px 10px', borderRadius: 4, background: statusStyle?.bg, ...fontMono, fontSize: 10, color: statusStyle?.text, fontWeight: 700 }}>
                      {statusStyle?.label}
                    </div>
                    <div style={{ flex: 1 }} />
                    <button onClick={() => handleReopen(mission)}
                      style={{ background: 'transparent', border: '1px solid rgba(45,200,150,0.4)', borderRadius: 6, padding: '5px 14px', ...fontMono, fontSize: 10, color: '#2DC896', cursor: 'pointer', letterSpacing: '0.06em', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.1)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      REOPEN
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 32 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(45,200,150,0.06)', border: '1px solid rgba(45,200,150,0.2)', borderRadius: 6, padding: '8px 16px', ...fontMono, fontSize: 11, color: page === 1 ? '#2DC89630' : '#2DC896', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>
              <ChevronLeft style={{ width: 13, height: 13 }} /> PREV
            </button>
            <span style={{ ...fontMono, fontSize: 11, color: '#8a9ab5' }}>PAGE {page} OF {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(45,200,150,0.06)', border: '1px solid rgba(45,200,150,0.2)', borderRadius: 6, padding: '8px 16px', ...fontMono, fontSize: 11, color: page === totalPages ? '#2DC89630' : '#2DC896', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>
              NEXT <ChevronRight style={{ width: 13, height: 13 }} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MissionHistory;

